import { resolveArticleUnitShortLabel } from './article_unit_codes.js?v=20260706d';

const ARTICLE_DATABASE_CATALOG_API = '/api/articles/catalog';
const ARTICLE_DATABASE_SEARCH_API = '/api/articles/search';

let loaded = false;
let loadingPromise = null;
const scopedLoadingPromises = new Map();
let lastError = null;
let articles = [];
let byArticleNo = new Map();
let setpositionsByParent = new Map();
let selectionOptionsByArticleNo = new Map();
let loadedArticleNos = new Set();
let loadedSetpositionParents = new Set();
let loadedSelectionParents = new Set();

const ArticleDatabaseCatalog = Object.freeze({
  load,
  ensureLoaded: load,
  ensureArticles,
  searchArticles,
  invalidate,
  isLoaded: () => loaded,
  lastError: () => lastError,
  all,
  get,
  getArticleByNo,
  resolveArticle,
  has,
  normalizeArticleNo,
  getArticleNo,
  getArticleSearchTerm,
  getArticleDescription1,
  getArticleDescription2,
  getArticleDescription3,
  getArticleUnit,
  getArticlePriceChf,
  getArticleSalePriceChf,
  getArticleWeightKg,
  isBaugruppenArtikel,
  isOffeneBaugruppe,
  isAuswahlArtikel,
  canDisplayAssemblyChildren,
  selectionOptionsFor,
  resolveSelectionArticleOption,
  setpositionsFor,
  hasSetpositions
});

const LEGACY_ARTICLE_NO_ALIASES = Object.freeze({
  'ms-distanzhalter': 'e39.12.0001'
});

function normalizeArticleNo(value) {
  const raw = value && typeof value === 'object'
    ? (value.ART_NRA || value.articleNo || value.number || value.articleNumber || value.articleKey)
    : value;
  const normalized = String(raw || '').trim().toLowerCase();
  return LEGACY_ARTICLE_NO_ALIASES[normalized] || normalized;
}

function normalizeNumericValue(value) {
  if (value == null || value === '') return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

function normalizeUnit(unit) {
  const value = String(unit || '').trim();
  const normalized = value.toLowerCase();
  if (['stk', 'stueck', 'stück', 'stuck', 'pcs', 'piece'].includes(normalized)) return 'Stk';
  if (['lfm', 'lm', 'laufmeter', 'm'].includes(normalized)) return 'lfm';
  if (['m2', 'm²', 'qm'].includes(normalized)) return 'm2';
  return value || 'Stk';
}

function normalizeFlagToken(value) {
  return String(value ?? '').trim().replace(/^['"]|['"]$/g, '').toUpperCase();
}

function isBaugruppenFlagValue(value) {
  const normalized = normalizeFlagToken(value);
  return ['B', '1', 'TRUE', 'WAHR', 'J', 'JA'].includes(normalized);
}

function isOffeneBaugruppeFlagValue(value) {
  return normalizeFlagToken(value) === '1';
}

function isAuswahlArtikelFlagValue(value) {
  const normalized = normalizeFlagToken(value);
  return ['1', 'TRUE', 'WAHR', 'J', 'JA'].includes(normalized);
}

function normalizeArticle(raw = {}) {
  const article = {
    ...raw,
    ART_NRA: normalizeArticleNo(raw.ART_NRA),
    articleNoDisplay: String(raw.ART_NRA || '').trim(),
    GEW_NETTO: normalizeNumericValue(raw.GEW_NETTO),
    Kalkulierter_Einstandspreis: normalizeNumericValue(raw.Kalkulierter_Einstandspreis),
    VKE_HANDEL: normalizeNumericValue(raw.VKE_HANDEL),
    VK_PREIS: normalizeNumericValue(raw.VK_PREIS),
    BAUGRP_FLAG: String(raw.BAUGRP_FLAG ?? 'A'),
    SET_FLAG: String(raw.SET_FLAG ?? '0'),
    SET_PREISFLAG: String(raw.SET_PREISFLAG ?? '0'),
    AUSWAHLARTIKEL_FLAG: String(raw.AUSWAHLARTIKEL_FLAG ?? '0'),
    currency: String(raw.ISO_CD || 'CHF').trim() || 'CHF',
    bomData: {}
  };
  return article;
}

function normalizeSetposition(raw = {}) {
  const parentArticleNo = normalizeArticleNo(raw.PARENT_ART_NRA);
  const childArticleNo = normalizeArticleNo(raw.CHILD_ART_NRA);
  if (!parentArticleNo || !childArticleNo) return null;
  const quantity = normalizeNumericValue(raw.MENGE) ?? 1;
  return {
    ...raw,
    PARENT_ART_NRA: parentArticleNo,
    CHILD_ART_NRA: childArticleNo,
    POS_NR: Number.isFinite(Number(raw.POS_NR)) ? Number(raw.POS_NR) : null,
    MENGE: quantity,
    SORT_ORDER: Number.isFinite(Number(raw.SORT_ORDER)) ? Number(raw.SORT_ORDER) : null,
    articleNo: childArticleNo,
    number: childArticleNo,
    qty: quantity,
    quantity
  };
}

function normalizeSelectionOption(raw = {}) {
  const sourceArticleNo = normalizeArticleNo(raw.AUSWAHL_ART_NRA);
  const optionArticleNo = normalizeArticleNo(raw.OPTION_ART_NRA);
  if (!sourceArticleNo || !optionArticleNo) return null;
  return {
    ...raw,
    AUSWAHL_ART_NRA: sourceArticleNo,
    OPTION_ART_NRA: optionArticleNo,
    SORT_ORDER: Number.isFinite(Number(raw.SORT_ORDER)) ? Number(raw.SORT_ORDER) : null,
    DEFAULT_FLAG: String(raw.DEFAULT_FLAG ?? '0'),
    ACTIVE_FLAG: String(raw.ACTIVE_FLAG ?? '1')
  };
}

function sortSetpositions(positions = []) {
  positions.sort((a, b) => {
    const aOrder = Number.isFinite(Number(a.SORT_ORDER)) ? Number(a.SORT_ORDER) : Number.MAX_SAFE_INTEGER;
    const bOrder = Number.isFinite(Number(b.SORT_ORDER)) ? Number(b.SORT_ORDER) : Number.MAX_SAFE_INTEGER;
    return aOrder - bOrder || (Number(a.POS_NR) || 0) - (Number(b.POS_NR) || 0) || a.CHILD_ART_NRA.localeCompare(b.CHILD_ART_NRA);
  });
}

function sortSelectionOptions(options = []) {
  options.sort((a, b) => {
    const aOrder = Number.isFinite(Number(a.SORT_ORDER)) ? Number(a.SORT_ORDER) : Number.MAX_SAFE_INTEGER;
    const bOrder = Number.isFinite(Number(b.SORT_ORDER)) ? Number(b.SORT_ORDER) : Number.MAX_SAFE_INTEGER;
    return aOrder - bOrder || a.OPTION_ART_NRA.localeCompare(b.OPTION_ART_NRA);
  });
}

function mergeCatalog(payload = {}, options = {}) {
  for (const rawArticle of payload.articles || []) {
    const article = normalizeArticle(rawArticle);
    if (!article.ART_NRA) continue;
    byArticleNo.set(article.ART_NRA, article);
    loadedArticleNos.add(article.ART_NRA);
  }

  for (const rawPosition of payload.setpositions || []) {
    const position = normalizeSetposition(rawPosition);
    if (!position) continue;
    if (!setpositionsByParent.has(position.PARENT_ART_NRA)) {
      setpositionsByParent.set(position.PARENT_ART_NRA, []);
    }
    const positions = setpositionsByParent.get(position.PARENT_ART_NRA);
    const existingIndex = positions.findIndex(item => item.POS_NR === position.POS_NR);
    if (existingIndex >= 0) positions[existingIndex] = position;
    else positions.push(position);
  }

  for (const rawOption of payload.selectionOptions || []) {
    const option = normalizeSelectionOption(rawOption);
    if (!option) continue;
    if (!selectionOptionsByArticleNo.has(option.AUSWAHL_ART_NRA)) {
      selectionOptionsByArticleNo.set(option.AUSWAHL_ART_NRA, []);
    }
    const optionsForArticle = selectionOptionsByArticleNo.get(option.AUSWAHL_ART_NRA);
    const existingIndex = optionsForArticle.findIndex(item => item.OPTION_ART_NRA === option.OPTION_ART_NRA);
    if (existingIndex >= 0) optionsForArticle[existingIndex] = option;
    else optionsForArticle.push(option);
  }

  for (const parentArticleNo of payload.requestedArticleNos || options.requestedArticleNos || []) {
    const key = normalizeArticleNo(parentArticleNo);
    if (!key) continue;
    loadedArticleNos.add(key);
    loadedSetpositionParents.add(key);
    loadedSelectionParents.add(key);
    if (!setpositionsByParent.has(key)) setpositionsByParent.set(key, []);
    if (!selectionOptionsByArticleNo.has(key)) selectionOptionsByArticleNo.set(key, []);
  }

  for (const positions of setpositionsByParent.values()) sortSetpositions(positions);
  for (const [articleNo, optionsForArticle] of selectionOptionsByArticleNo.entries()) {
    sortSelectionOptions(optionsForArticle);
    const article = byArticleNo.get(articleNo);
    if (article) article.bomData.selectionOptions = optionsForArticle.slice();
  }

  articles = Array.from(byArticleNo.values()).sort((a, b) => a.ART_NRA.localeCompare(b.ART_NRA));
  lastError = null;
}

function indexCatalog(payload = {}) {
  articles = [];
  byArticleNo = new Map();
  setpositionsByParent = new Map();
  selectionOptionsByArticleNo = new Map();
  loadedArticleNos = new Set();
  loadedSetpositionParents = new Set();
  loadedSelectionParents = new Set();
  mergeCatalog(payload);
  for (const articleNo of loadedArticleNos) {
    loadedSetpositionParents.add(articleNo);
    loadedSelectionParents.add(articleNo);
    if (!setpositionsByParent.has(articleNo)) setpositionsByParent.set(articleNo, []);
    if (!selectionOptionsByArticleNo.has(articleNo)) selectionOptionsByArticleNo.set(articleNo, []);
  }
  loaded = true;
}

function mergeArticles(rawArticles = []) {
  mergeCatalog({ articles: rawArticles });
}

function load(options = {}) {
  if (loaded && options.force !== true) return ArticleDatabaseCatalog;
  if (loadingPromise && options.force !== true) return loadingPromise;

  loadingPromise = fetch(ARTICLE_DATABASE_CATALOG_API, { cache: 'no-store' })
    .then(async response => {
      const payload = await response.json().catch(() => ({}));
      if (!response.ok || payload.ok === false) {
        throw new Error(payload.error || `Artikelkatalog konnte nicht geladen werden (${response.status}).`);
      }
      indexCatalog(payload);
      return ArticleDatabaseCatalog;
    })
    .catch(error => {
      lastError = error;
      throw error;
    })
    .finally(() => {
      loadingPromise = null;
    });

  return loadingPromise;
}

function ensureArticles(articleNos = [], options = {}) {
  const normalized = [...new Set((articleNos || []).map(normalizeArticleNo).filter(Boolean))];
  const missing = options.force === true
    ? normalized
    : normalized.filter(articleNo => (
      !loadedArticleNos.has(articleNo)
      || !loadedSetpositionParents.has(articleNo)
      || !loadedSelectionParents.has(articleNo)
    ));
  if (!missing.length) return ArticleDatabaseCatalog;

  const cacheKey = missing.slice().sort().join('|');
  if (scopedLoadingPromises.has(cacheKey) && options.force !== true) {
    return scopedLoadingPromises.get(cacheKey);
  }

  const promise = fetch(ARTICLE_DATABASE_CATALOG_API, {
    method: 'POST',
    cache: 'no-store',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ articleNos: missing })
  })
    .then(async response => {
      const payload = await response.json().catch(() => ({}));
      if (!response.ok || payload.ok === false) {
        throw new Error(payload.error || `Artikel konnten nicht geladen werden (${response.status}).`);
      }
      mergeCatalog(payload, { requestedArticleNos: missing });
      return ArticleDatabaseCatalog;
    })
    .catch(error => {
      lastError = error;
      throw error;
    })
    .finally(() => {
      scopedLoadingPromises.delete(cacheKey);
    });

  scopedLoadingPromises.set(cacheKey, promise);
  return promise;
}

async function searchArticles(query = '', options = {}) {
  const normalizedQuery = String(query || '').trim();
  if (normalizedQuery.length < 2) return [];
  const limit = Math.max(1, Math.min(50, Number.parseInt(options.limit || 25, 10) || 25));
  const url = `${ARTICLE_DATABASE_SEARCH_API}?query=${encodeURIComponent(normalizedQuery)}&limit=${limit}`;
  const response = await fetch(url, { cache: 'no-store' });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok || payload.ok === false) {
    throw new Error(payload.error || `Artikelsuche fehlgeschlagen (${response.status}).`);
  }
  const found = (payload.articles || []).map(normalizeArticle).filter(article => article.ART_NRA);
  mergeArticles(found);
  return found;
}

function invalidate() {
  loaded = false;
  loadingPromise = null;
  scopedLoadingPromises.clear();
  lastError = null;
  articles = [];
  byArticleNo = new Map();
  setpositionsByParent = new Map();
  selectionOptionsByArticleNo = new Map();
  loadedArticleNos = new Set();
  loadedSetpositionParents = new Set();
  loadedSelectionParents = new Set();
}

function all() {
  return articles.slice();
}

function get(articleNo) {
  const key = normalizeArticleNo(articleNo);
  return byArticleNo.get(key) || null;
}

function getArticleByNo(articleNo) {
  return get(articleNo);
}

function resolveArticle(articleNo) {
  return get(articleNo);
}

function has(articleNo) {
  return Boolean(get(articleNo));
}

function setpositionsFor(articleNo) {
  const key = normalizeArticleNo(articleNo);
  return (setpositionsByParent.get(key) || []).map(position => ({ ...position }));
}

function hasSetpositions(articleNo) {
  return setpositionsByParent.has(normalizeArticleNo(articleNo));
}

function selectionOptionsFor(articleOrNumber, context = {}) {
  const articleNo = normalizeArticleNo(articleOrNumber);
  const dbOptions = selectionOptionsByArticleNo.get(articleNo);
  const contextOptions = context.selectionOptionsByArticleNo?.[articleNo]
    || context.selectionOptions?.[articleNo]
    || context.selectionOptions;
  const rawOptions = dbOptions || (Array.isArray(contextOptions) ? contextOptions : []);
  return rawOptions
    .map(normalizeSelectionOption)
    .filter(Boolean)
    .filter(option => normalizeFlagToken(option.ACTIVE_FLAG) !== '0')
    .sort((a, b) => {
      const aOrder = Number.isFinite(Number(a.SORT_ORDER)) ? Number(a.SORT_ORDER) : Number.MAX_SAFE_INTEGER;
      const bOrder = Number.isFinite(Number(b.SORT_ORDER)) ? Number(b.SORT_ORDER) : Number.MAX_SAFE_INTEGER;
      return aOrder - bOrder || a.OPTION_ART_NRA.localeCompare(b.OPTION_ART_NRA, 'de');
    });
}

function resolveSelectionArticleOption(articleOrNumber, context = {}) {
  const article = typeof articleOrNumber === 'string' ? get(articleOrNumber) : articleOrNumber;
  if (!article) return { article: null, error: 'Auswahlartikel nicht gefunden.' };
  if (!isAuswahlArtikel(article)) return { article, optionArticleNo: getArticleNo(article), sourceArticleNo: '' };

  const sourceArticleNo = getArticleNo(article);
  const selectedFromMap = context.selectionChoices?.[sourceArticleNo]
    || context.configurationSelectionChoices?.[sourceArticleNo];
  const selectedOptionNo = normalizeArticleNo(
    context.SELECTED_OPTION_ART_NRA
    || context.selectedOptionArticleNo
    || selectedFromMap
    || ''
  );
  const options = selectionOptionsFor(article, context);
  let chosen;
  if (selectedOptionNo) {
    chosen = options.find(option => option.OPTION_ART_NRA === selectedOptionNo) || null;
    if (!chosen) {
      return {
        article: null,
        sourceArticleNo,
        error: `Auswahl ${sourceArticleNo}: Option ${selectedOptionNo} ist nicht aktiv oder nicht zugeordnet.`
      };
    }
  } else {
    chosen = options.find(option => isAuswahlArtikelFlagValue(option.DEFAULT_FLAG)) || null;
    if (!chosen) {
      return {
        article: null,
        sourceArticleNo,
        error: `Auswahl ${sourceArticleNo}: Keine Auswahl und kein Default gepflegt.`
      };
    }
  }
  const optionArticle = get(chosen.OPTION_ART_NRA);
  if (!optionArticle) {
    return {
      article: null,
      sourceArticleNo,
      optionArticleNo: chosen.OPTION_ART_NRA,
      error: `Auswahl ${sourceArticleNo}: Option ${chosen.OPTION_ART_NRA} ist nicht im Artikelstamm vorhanden.`
    };
  }
  return {
    article: optionArticle,
    sourceArticleNo,
    optionArticleNo: chosen.OPTION_ART_NRA,
    option: chosen,
    isDefault: !selectedOptionNo
  };
}

function getArticleNo(article) {
  return normalizeArticleNo(article);
}

function getArticleSearchTerm(article) {
  return String(article?.ART_BEZ || '').trim();
}

function getArticleDescription1(article) {
  return String(article?.BES1_DE || '').trim();
}

function getArticleDescription2(article) {
  return String(article?.BES2_DE || '').trim();
}

function getArticleDescription3(article) {
  return String(article?.BES3_DE || '').trim();
}

function getArticleUnit(article) {
  return normalizeUnit(resolveArticleUnitShortLabel(article?.VKE_HANDEL) || article?.unit || 'Stk');
}

function getArticlePriceChf(articleOrNumber) {
  const article = typeof articleOrNumber === 'string' ? get(articleOrNumber) : articleOrNumber;
  if (!article) return 0;
  const costPrice = normalizeNumericValue(article.Kalkulierter_Einstandspreis);
  if (Number.isFinite(costPrice)) return costPrice;
  const sellingPrice = normalizeNumericValue(article.VK_PREIS);
  return Number.isFinite(sellingPrice) ? sellingPrice : 0;
}

function getArticleSalePriceChf(articleOrNumber) {
  const article = typeof articleOrNumber === 'string' ? get(articleOrNumber) : articleOrNumber;
  if (!article) return 0;
  const sellingPrice = normalizeNumericValue(article.VK_PREIS);
  return Number.isFinite(sellingPrice) ? sellingPrice : 0;
}

function getArticleWeightKg(articleOrNumber) {
  const article = typeof articleOrNumber === 'string' ? get(articleOrNumber) : articleOrNumber;
  const weight = normalizeNumericValue(article?.GEW_NETTO);
  return Number.isFinite(weight) && weight > 0 ? weight : 0;
}

function isBaugruppenArtikel(articleOrNumber) {
  const article = typeof articleOrNumber === 'string' ? get(articleOrNumber) : articleOrNumber;
  return Boolean(article && isBaugruppenFlagValue(article.BAUGRP_FLAG));
}

function isOffeneBaugruppe(articleOrNumber) {
  const article = typeof articleOrNumber === 'string' ? get(articleOrNumber) : articleOrNumber;
  return Boolean(article && isBaugruppenArtikel(article) && isOffeneBaugruppeFlagValue(article.SET_FLAG));
}

function isAuswahlArtikel(articleOrNumber) {
  const article = typeof articleOrNumber === 'string' ? get(articleOrNumber) : articleOrNumber;
  return Boolean(article && isAuswahlArtikelFlagValue(article.AUSWAHLARTIKEL_FLAG));
}

function canDisplayAssemblyChildren(articleOrNumber, context = {}) {
  const article = typeof articleOrNumber === 'string' ? get(articleOrNumber) : articleOrNumber;
  if (!isBaugruppenArtikel(article)) return false;
  if (!isOffeneBaugruppe(article)) return false;
  const assemblyDepth = Number(context.assemblyDepth ?? 0);
  const isRootArticle = context.isRootArticle === true;
  if (!isRootArticle && assemblyDepth >= 1) return false;
  return true;
}

export { ArticleDatabaseCatalog };
